import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  advanceLineKdsMutation,
  getKitchenOrdersQuery,
  getOrderLinesQuery,
  nextKdsStatus,
  queryKeys,
} from "@comandero/shared";
import type { Branch, Order, OrderLine, Organization } from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useKdsLineSwipe } from "../hooks/use-kds-line-swipe";

interface KdsBoardProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

/** Etiqueta del estado de cocina (pending → preparing → ready → served). */
function statusLabel(
  t: (m: MessageDescriptor) => string,
  status: string | null | undefined,
): string {
  switch (status) {
    case "cooking": {
      return t(msg`Preparing`);
    }
    case "ready": {
      return t(msg`Ready`);
    }
    case "served": {
      return t(msg`Served`);
    }
    default: {
      return t(msg`Pending`);
    }
  }
}

/**
 * Clases del chip de estado de KDS — codificadas por color (identidad Comandero):
 * pendiente=ámbar, preparando=azul, listo=verde, servido=neutro.
 */
function statusChipClass(status: string | null | undefined): string {
  switch (status) {
    case "cooking": {
      return "bg-kds-cooking text-kds-cooking-foreground";
    }
    case "ready": {
      return "bg-kds-ready text-kds-ready-foreground";
    }
    case "served": {
      return "bg-kds-served text-kds-served-foreground";
    }
    default: {
      return "bg-kds-pending text-kds-pending-foreground";
    }
  }
}

export function KdsBoard(props: KdsBoardProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;

  const orders = createPowerSyncQuery<Order>({
    queryKey: () => queryKeys.comandero.kitchenOrders(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orgId();
      const bid = branchId();
      return d && oid && bid ? getKitchenOrdersQuery(d, oid, bid) : null;
    },
    db,
    enabled: () => Boolean(orgId()) && Boolean(branchId()),
  });

  return (
    <div class="flex flex-col gap-4" data-testid="kds-board">
      <Show
        when={(orders.data ?? []).length > 0}
        fallback={
          <div
            class="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 bg-surface/40 px-6 py-16 text-center"
            data-testid="kds-empty"
          >
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="h-7 w-7"
                aria-hidden="true"
              >
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <p class="text-base font-semibold text-foreground">{t(msg`All caught up`)}</p>
            <p class="max-w-xs text-sm text-muted-foreground">
              {t(msg`No orders in the kitchen.`)}
            </p>
          </div>
        }
      >
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <For each={orders.data}>{(o) => <KdsTicket order={o} />}</For>
        </div>
      </Show>
    </div>
  );
}

function KdsTicket(props: { order: Order }) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const lines = createPowerSyncQuery<OrderLine>({
    queryKey: () => queryKeys.comandero.kitchenLines(props.order.id),
    queryFn: () => {
      const d = drizzle();
      return d ? getOrderLinesQuery(d, props.order.id) : null;
    },
    db,
  });

  async function advance(line: OrderLine) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await advanceLineKdsMutation(d, line.id, line.kds_status);
  }

  return (
    <article
      class="flex animate-completed-reveal flex-col overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-sm shadow-black/5"
      data-testid="kds-ticket"
      data-order-id={props.order.id}
    >
      <header class="flex items-center justify-between border-b border-border/60 bg-surface-secondary/60 px-4 py-2.5">
        <span class="text-sm font-bold tracking-tight text-foreground">
          {t(msg`Order`)}{" "}
          <span class="font-numeric text-accent">#{props.order.id.slice(0, 6)}</span>
        </span>
      </header>
      <ul class="flex flex-col divide-y divide-border/40">
        <For each={lines.data}>{(l) => <KdsLine line={l} onAdvance={() => void advance(l)} />}</For>
      </ul>
    </article>
  );
}

/**
 * Una línea del KDS con swipe-to-advance (bump): deslizar a la derecha avanza el
 * estado de cocina (pending → cooking → ready → served), patrón estándar de las
 * pantallas de cocina. El botón "Advance" sigue ahí para teclado/ratón; el chip
 * de estado anuncia el cambio. Mismo estándar lib/gestures (haptics,
 * reduced-motion, touch-action).
 */
function KdsLine(props: { line: OrderLine; onAdvance: () => void }) {
  const { t } = useI18n();
  let surfaceRef: HTMLDivElement | undefined;

  const canAdvance = (): boolean => Boolean(nextKdsStatus(props.line.kds_status));

  const swipe = useKdsLineSwipe({
    enabled: () => canAdvance(),
    onAdvance: () => props.onAdvance(),
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <li class="relative overflow-hidden" data-testid="kds-line" data-line-id={props.line.id}>
      {/* Underlay: affordance de "bump" revelada al deslizar a la derecha. */}
      <Show when={canAdvance()}>
        <div
          class="pointer-events-none absolute inset-y-0 left-0 flex items-center gap-1.5 pl-4 text-sm font-semibold text-success"
          aria-hidden="true"
          style={{ opacity: swipe.tx() > 0 ? 1 : 0 }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            class="h-5 w-5"
          >
            <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {t(msg`Advance`)}
        </div>
      </Show>

      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        class="flex items-center justify-between gap-3 bg-surface px-4 py-3 touch-pan-y"
        style={{
          transform: `translate3d(${swipe.tx()}px, 0, 0)`,
          transition: swipe.isAnimating()
            ? "transform 220ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        <span class="text-base font-medium leading-tight">
          <span class="font-numeric text-muted-foreground">{props.line.qty}×</span>{" "}
          {props.line.name}
        </span>
        <span class="flex shrink-0 items-center gap-2">
          <span
            class={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusChipClass(props.line.kds_status)}`}
            data-testid="kds-line-status"
          >
            {statusLabel(t, props.line.kds_status)}
          </span>
          <Show when={canAdvance()}>
            <Button
              size="sm"
              variant="secondary"
              class="h-11 min-w-11 px-3"
              data-testid="kds-advance-btn"
              onClick={() => props.onAdvance()}
            >
              {t(msg`Advance`)}
            </Button>
          </Show>
        </span>
      </div>
    </li>
  );
}
